import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { Viewer } from "components/auth/types";
import ContainerWithSidebar from "components/common/layout/ContainerWithSidebar";
import { navigate } from "utils/navigation";

import OrgSettings from "../settings/pages/organization";
import ScheduledUpdates from "../settings/pages/scheduled-updates";
import TeamSettings from "../settings/pages/team";
import TeamSettingsNew from "../settings/pages/team/create";
import TeamSettingsUpdate from "../settings/pages/team/update";
import TemplatesNew from "../template-groups/new";
import TemplatesUpdate from "../template-groups/update";
import TemplateSettings from "../settings/pages/templates";
import ContractsSettings from "../settings/pages/contracts";
import ContractsIndexPage from "../settings/pages/contracts-index";
import { ManagerContractsRouteGate } from "../settings/pages/manager-contracts-route-gate";
import PropertySettingsPage from "../settings/property";
import ImportsPage from "./pages/imports";
import PropertyNewPage from "./property/new";
import Sidebar from "./sidebar";
import { sortAndFilterLists } from "./list-utils";

type Props = { viewer: Viewer; }

const getFirstListId = (viewer:Viewer):string =>
  sortAndFilterLists(viewer, viewer.lists ?? [])[0].id;

const SettingsPage: React.FC<Props> = ({ viewer }) => {
  const location = useLocation();
  if (location.pathname.match("/dash/manager/settings[/]?$")) {
    const redirectUrl = viewer?.isSupervisor ? "/dash/manager/settings/organization" : `/dash/manager/settings/property/${getFirstListId(viewer)}/lists`;
    navigate(redirectUrl);
  }

  return <ContainerWithSidebar>
    <Sidebar viewer={viewer} />
    <div style={{ width: "100%" }}>
      <Routes>
        <Route path="organization" element={<OrgSettings viewer={viewer} />} />
        <Route
          path="contracts/:contractId"
          element={(
            <ManagerContractsRouteGate viewer={viewer}>
              <ContractsSettings />
            </ManagerContractsRouteGate>
          )}
        />
        <Route
          path="contracts"
          element={(
            <ManagerContractsRouteGate viewer={viewer}>
              <ContractsIndexPage />
            </ManagerContractsRouteGate>
          )}
        />
        <Route path="property/new/*" element={<PropertyNewPage />} />
        <Route path="property/:listId/*" element={<PropertySettingsPage viewer={viewer} />} />
        <Route path="team" element={<TeamSettings viewer={viewer} />} />
        <Route path="team/new" element={<TeamSettingsNew viewer={viewer} />} />
        <Route path="team/:userId" element={<TeamSettingsUpdate />} />
        <Route path="scheduled-updates" element={<ScheduledUpdates viewer={viewer} />} />
        <Route path="imports/*" element={<ImportsPage viewer={viewer} />} />
        <Route path="templates" element={<TemplateSettings viewer={viewer} />} />
        <Route path="templates/new" element={<TemplatesNew viewer={viewer} />} />
        <Route path="templates/update/:id" element={<TemplatesUpdate viewer={viewer} />} />
        <Route path="" element={<Navigate to={viewer?.isSupervisor ? "/dash/manager/settings/organization" : `/dash/manager/settings/property/${getFirstListId(viewer)}/lists`} replace />} />
      </Routes>
    </div>
  </ContainerWithSidebar>;
};

export default SettingsPage;
